/**
 * Repository Provider Constants (Frontend)
 * 
 * Metadata and configuration for supported repository providers
 * This mirrors the backend constants for frontend use
 */

/**
 * Provider authentication type
 */
export enum ProviderAuthType {
  OAUTH = 'oauth',
  CREDENTIALS = 'credentials',
}

/**
 * Provider metadata
 */
interface ProviderMetadata {
  name: string;
  displayName: string;
  authType: ProviderAuthType;
  icon: string;
  logo: string;
}

/**
 * Repository provider metadata
 */
export const REPOSITORY_PROVIDER_METADATA: Record<string, ProviderMetadata> = {
  'google_drive': {
    name: 'google_drive',
    displayName: 'Google Drive',
    authType: ProviderAuthType.OAUTH,
    icon: '📁',
    logo: '/providers/google-drive.svg',
  },
  'sharepoint': {
    name: 'sharepoint',
    displayName: 'SharePoint',
    authType: ProviderAuthType.OAUTH,
    icon: '📊',
    logo: '/providers/sharepoint.svg',
  },
  'dropbox': {
    name: 'dropbox',
    displayName: 'Dropbox',
    authType: ProviderAuthType.OAUTH,
    icon: '📦',
    logo: '/providers/dropbox.svg',
  },
  's3': {
    name: 's3',
    displayName: 'Amazon S3',
    authType: ProviderAuthType.CREDENTIALS,
    icon: '☁️',
    logo: '/providers/s3.svg',
  },
  'box': {
    name: 'box',
    displayName: 'Box',
    authType: ProviderAuthType.OAUTH,
    icon: '📂',
    logo: '/providers/box.svg',
  },
  // The LMS destinations. Absent until now, which is why they rendered as a generic folder
  // emoji anywhere this map is the logo source — the upload page among them.
  //
  // `CREDENTIALS` rather than `OAUTH` matters beyond the label: `isOAuthProvider` reads this
  // map, and several per-user connection affordances key off it (reconnect, "your status",
  // the write-scope upgrade). Both of these hold one credential belonging to the connection,
  // not a per-user grant, so OAUTH here would offer a reconnect flow that does not exist.
  // S3 is the precedent.
  'onpoint': {
    name: 'onpoint',
    displayName: 'OnPoint Digital',
    authType: ProviderAuthType.CREDENTIALS,
    icon: '🎓',
    logo: '/lms/onpoint.svg',
  },
  'moodle': {
    name: 'moodle',
    displayName: 'Moodle',
    authType: ProviderAuthType.CREDENTIALS,
    icon: '🎓',
    logo: '/lms/moodle.svg',
  },
  // A content hub rather than an LMS, but it sits with them for the same reason: one
  // credential belonging to the connection, entered as a hostname and a token. `CREDENTIALS`
  // matters beyond the label — `isOAuthProvider` reads this map, and OAUTH here would offer
  // a reconnect flow Acto has none of, since its Personal Access Token is issued in Acto's
  // own admin UI and never rotates.
  'acto': {
    name: 'acto',
    displayName: 'Acto',
    authType: ProviderAuthType.CREDENTIALS,
    icon: '📚',
    logo: '/lms/acto.png',
  },
};

/**
 * Check if a provider uses OAuth authentication
 * @param provider Provider name
 * @returns true if provider uses OAuth, false if uses credentials
 */
/** One spelling for a provider id, so comparisons cannot miss on case or padding. */
export function normalizeProviderId(provider: string): string {
  return String(provider || '').trim().toLowerCase();
}

/**
 * Learning platforms, as opposed to file storage.
 *
 * Governs presentation and setup: which section of the Content Repositories page a
 * connection's card appears in, and therefore which credentials form its Configure button
 * opens. A Moodle card outside this list would land among the cloud repositories, where
 * nothing knows how to collect a site address and a web service token.
 *
 * Deliberately NOT the same question as {@link TARGET_ONLY_PROVIDERS}. Being a learning
 * platform says nothing about whether content can be read back out — see that list.
 */
export const LMS_PROVIDERS = ['onpoint', 'moodle', 'acto'];

export function isLmsProvider(provider: string): boolean {
  return LMS_PROVIDERS.includes(normalizeProviderId(provider));
}

/**
 * Providers that are push destinations only — they cannot be browsed, discovered from, or
 * synced, so they must not be offered as an ingestion source.
 *
 * Mirrors `isTargetOnlyProvider` on the server, which derives the same answer from the
 * target-profile registry. Kept as an explicit list here because the browser has no registry
 * to derive it from, and getting it wrong is not cosmetic: a destination-only connection
 * offered as an upload source gives a Browse button that cannot work.
 *
 * `moodle` left this list in METALARK-1168, which made it browsable, while staying in
 * {@link LMS_PROVIDERS} — it is a learning platform that content also comes *out* of. That
 * divergence is the reason these are two lists: one list doing both jobs would have moved
 * the Moodle card out of the LMS section and away from its credentials form the moment it
 * became browsable. OnPoint remains here because its API offers no ingestion surface.
 *
 * `acto` is here for now but will follow Moodle out of it: its API *can* list a folder's
 * resources, so it is browsable in principle, and METALARK-1224 is what promotes it. Until
 * then offering Browse on an Acto connection would give a button with nothing behind it.
 */
export const TARGET_ONLY_PROVIDERS = ['onpoint', 'acto'];

export function isTargetOnlyProvider(provider: string): boolean {
  return TARGET_ONLY_PROVIDERS.includes(normalizeProviderId(provider));
}

export function isOAuthProvider(provider: string): boolean {
  const metadata = REPOSITORY_PROVIDER_METADATA[normalizeProviderId(provider)];
  return metadata?.authType === ProviderAuthType.OAUTH;
}

/**
 * Check if a provider uses credential-based authentication
 * @param provider Provider name
 * @returns true if provider uses credentials, false if uses OAuth
 */
export function isCredentialProvider(provider: string): boolean {
  const metadata = REPOSITORY_PROVIDER_METADATA[normalizeProviderId(provider)];
  return metadata?.authType === ProviderAuthType.CREDENTIALS;
}

/**
 * Get provider display name
 * @param provider Provider name
 * @returns Display name for the provider
 */
export function getProviderDisplayName(provider: string): string {
  const metadata = REPOSITORY_PROVIDER_METADATA[normalizeProviderId(provider)];
  return metadata?.displayName || provider;
}

/**
 * Get provider icon
 * @param provider Provider name
 * @returns Icon emoji for the provider
 */
export function getProviderIcon(provider: string): string {
  const metadata = REPOSITORY_PROVIDER_METADATA[normalizeProviderId(provider)];
  return metadata?.icon || '📁';
}

/**
 * Get provider logo path
 * @param provider Provider name
 * @returns Logo path for the provider
 */
export function getProviderLogoPath(provider: string): string {
  const metadata = REPOSITORY_PROVIDER_METADATA[normalizeProviderId(provider)];
  return metadata?.logo || '';
}
